import { converter } from "culori";
import omit from "lodash/omit";
import { useTheme } from "../composables/theme";
import { objectKeys } from "../utils/object";
import * as dark from "./dark";
import * as light from "./light";

export const colors = objectKeys(omit(light));
export type ThemeColor = (typeof colors)[number];

export function getThemeColors() {
  const colorsToOmit = ["mauve", "slate", "olive", "sand", "sage", "brand"];

  return colors.filter((color) => !colorsToOmit.includes(color));
}

export const shades = [...Array.from({ length: 12 }, (_, i) => i + 1)];

export const utilityColors = ["info", "warn", "danger", "success"] as const;
export type UtilityColor = (typeof utilityColors)[number];

interface ColorOptions {
  shade?: number;
  opacity?: number;
  theme?: "light" | "dark";
}

export const getDarkColor = generateGetColor(dark);
export const getLightColor = generateGetColor(light);

export const getColor = (color: ThemeColor, options?: ColorOptions) => {
  const shade = options?.shade ?? 9;
  const opacity = options?.opacity ?? 1;

  const theme = options?.theme ?? useTheme().theme.value;

  if (theme === "dark") {
    return getDarkColor(color, shade, opacity);
  }

  return getLightColor(color, shade, opacity);
};

function generateGetColor(pallete: typeof light) {
  return (color: ThemeColor, _shade: number, _opacity: number) => {
    if (color in pallete) {
      //@ts-ignore
      const shade = pallete[color]?.[`${color}${_shade}`];
      const rgbConverter = converter("rgb");
      const rgb = rgbConverter(`oklch(${shade})`);

      if (!rgb) return "";
      const minZero = (num: number) => (num < 0 ? 0 : num);
      const r = minZero(Math.round(rgb.r * 255));
      const g = minZero(Math.round(rgb.g * 255));
      const b = minZero(Math.round(rgb.b * 255));

      return `rgba(${minZero(r)}, ${g}, ${b}, ${_opacity})`;
    }

    return color;
  };
}

export function getRandomColor(options?: ColorOptions) {
  const color = colors[Math.floor(Math.random() * colors.length)];

  return getColor(color, options);
}

export function getColorFromString(str: string): ThemeColor {
  const palette = getThemeColors();
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.codePointAt(i)! + ((hash << 5) - hash);
  }
  return palette[Math.abs(hash) % palette.length];
}

const colorBasket = [...getThemeColors()];
export function getColorFromBasket(options?: ColorOptions) {
  const color = colorBasket.shift();

  if (!color) {
    colorBasket.push(...getThemeColors());
    return getColorFromBasket(options);
  }

  return getColor(color, options);
}
